'use client';

import { useRouter } from 'next/router';
import { web2app } from '@kcd/app-interface';
import { TopNavigation } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import { MYPAGE_ROUTER, SEARCH_ROUTER } from '~/constants/router';
import { useCurrentUser } from '~/domains/auth/queries';
import ShoppingCartLink from '~/domains/cart/ShoppingCartLink';
import PlusMembershipTopBadgeContainer from '~/domains/plusMembership/components/PlusMembershipTopBadge';
import { useLinkComponent } from '~/hooks/mobileApp';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';
import { openPageModal } from '~/utils/mobileApp';
import { HOME_BASE_URL } from '~/utils/url';

import { CashnyHeaderButton } from './components/CashnyHeaderButton';
import CouponTooltip from './components/CouponTooltip';

export const HOME_TOP_SEARCH_BAR_HEIGHT = 56;

function MenuButton() {
  const handleOpenMenu = () => {
    trackEvent('clicked header menu', {
      name: '전체메뉴',
      page: '마켓',
    });
    web2app.openModal({ url: `${HOME_BASE_URL}/menu` });
  };

  return (
    <button type="button" onClick={handleOpenMenu}>
      <TopNavigation.WrappedIcon key="user" icon="menu" />
    </button>
  );
}

const MarketTextSVG = () => (
  <svg
    width="35"
    height="19"
    viewBox="0 0 35 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.2787 7.26139H17.0342V9.44406H14.2787V18.8926H11.6609V0.213052H14.2787V7.26139ZM9.11568 14.3315H0.145718V1.98964H9.11568V14.3315ZM6.51243 4.04178H2.72721V12.2576H6.51243V4.04178ZM31.3621 0.242057H33.8493V12.6999H31.3621V0.242057ZM27.4971 7.48619H24.6546C23.5596 9.50932 21.7033 11.1409 18.8172 12.3809L17.5772 10.5318C19.7889 9.59634 21.2537 8.46512 22.1746 7.12362L18.1211 7.5587L17.7948 5.44855L23.081 5.21651C23.2261 4.74517 23.3203 4.25207 23.3856 3.73722H18.5852V1.64157H25.9453C25.9453 2.99033 25.8076 4.23757 25.4885 5.39054H27.4971V0.539364H29.9699V11.0394H27.4971V7.48619ZM28.6066 11.663V12.0546C28.5993 14.114 30.6732 16.2459 34.6833 16.7752L33.6971 18.8056C30.6805 18.356 28.4833 17.0363 27.3231 15.2452C26.1556 17.0363 23.9657 18.356 20.9636 18.8056L19.9049 16.7752C23.9512 16.2459 26.0179 14.114 26.0251 12.0546V11.663H28.6066Z"
      fill="#0C1120"
    />
  </svg>
);

function StaticSearchBar() {
  return (
    <div
      className="box-border flex w-full items-center rounded-full bg-input-02 px-12 text-icon"
      style={{
        height: '44px',
        border: '1px solid #E8EDF2',
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          minWidth: '24px',
        }}
      >
        <path
          d="M10 2.75C14.0041 2.75 17.25 5.99594 17.25 10C17.25 11.7319 16.6427 13.3219 15.6295 14.5688L20.5303 19.4697C20.8232 19.7626 20.8232 20.2374 20.5303 20.5303C20.2641 20.7966 19.8474 20.8208 19.5538 20.6029L19.4697 20.5303L14.5688 15.6295C13.3219 16.6427 11.7319 17.25 10 17.25C5.99594 17.25 2.75 14.0041 2.75 10C2.75 5.99594 5.99594 2.75 10 2.75ZM10 4.25C6.82436 4.25 4.25 6.82436 4.25 10C4.25 13.1756 6.82436 15.75 10 15.75C13.1756 15.75 15.75 13.1756 15.75 10C15.75 6.82436 13.1756 4.25 10 4.25Z"
          fill="currentColor"
        />
      </svg>
      <div className="w-4 flex-none" />
      <svg
        width="132"
        height="15"
        viewBox="0 0 132 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.4254 0.381215V4.00552H13.5138V5.03315H11.4254V8.83425H10.1989V0.381215H11.4254ZM4.63536 4.74586C4.03867 6.38674 2.79558 7.66851 1.17127 8.29282L0.508287 7.30939C2.59669 6.53591 3.98895 4.67956 4 2.53591V1.09945H5.20442V2.62431C5.20442 4.51381 6.64088 6.24862 8.65193 6.9337L7.98895 7.90608C6.45304 7.34254 5.23757 6.19337 4.63536 4.74586ZM6.97238 9.18785C9.8232 9.18785 11.547 10.116 11.547 11.7072C11.547 13.3204 9.8232 14.2376 6.97238 14.2376C4.1105 14.2376 2.36464 13.3204 2.36464 11.7072C2.36464 10.116 4.1105 9.18785 6.97238 9.18785ZM6.97238 13.2652C9.07182 13.2541 10.3536 12.6906 10.3536 11.7072C10.3536 10.7569 9.07182 10.1713 6.97238 10.1713C4.87293 10.1713 3.58011 10.7569 3.59116 11.7072C3.58011 12.6906 4.87293 13.2541 6.97238 13.2652ZM15.6182 4.76796H17.7176V1.83978H15.574V0.834253H25.8392V1.83978H23.7066V4.76796H25.795V5.78453H15.6182V4.76796ZM18.9331 4.76796H22.4801V1.83978H18.9331V4.76796ZM27.0546 7.09945V8.10497H21.3198V9.75138H25.4966V14.0829H15.9386V9.75138H20.1154V8.10497H14.3917V7.09945H27.0546ZM17.1541 13.0773H24.3033V10.7238H17.1541V13.0773ZM34.5402 5.45304C31.4463 5.44199 29.6231 4.59116 29.6231 3.0221C29.6231 1.47514 31.4463 0.624309 34.5402 0.624309C37.6342 0.624309 39.4463 1.47514 39.4463 3.0221C39.4463 4.59116 37.6342 5.44199 34.5402 5.45304ZM34.5402 1.55249C32.2419 1.55249 30.8938 2.10497 30.9049 3.0221C30.8938 3.98343 32.2419 4.51381 34.5402 4.51381C36.8496 4.51381 38.1977 3.98343 38.1977 3.0221C38.1977 2.10497 36.8496 1.55249 34.5402 1.55249ZM28.2198 7.38674V6.39226H40.8828V7.38674H28.2198ZM39.2474 11.6961H30.9933V13.1436H39.6673V14.116H29.7888V10.7901H38.043V9.53039H29.7557V8.59116H39.2474V11.6961ZM56.3508 3.97237V0.392264H57.5773V8.31492H56.3508V5H53.268V3.97237H56.3508ZM53.2348 1.28729C53.2238 4.76796 50.7376 7.26519 46.7376 8.40331L46.2182 7.44199C49.5331 6.50276 51.6547 4.66298 51.9254 2.30387H46.9365V1.28729H53.2348ZM57.5773 14.0718H48.5608V8.90055H57.5773V14.0718ZM56.3619 9.88398H49.7541V13.0773H56.3619V9.88398ZM70.5215 4.01657V0.392264H71.6817V8.77901H70.5215V5.0442H68.6651V8.70166H67.516V0.646408H68.6651V4.01657H70.5215ZM66.8751 6.96685L66.2232 7.91713C64.8972 7.38122 63.9469 6.35912 63.4497 5.1326C62.9414 6.47514 61.9414 7.66298 60.5767 8.27072L59.9027 7.30939C61.737 6.50276 62.853 4.67956 62.853 3.09945V1.23204H64.0353V3.09945C64.0353 4.67956 65.0961 6.27072 66.8751 6.96685ZM70.4773 10.558H62.3226V9.55249H71.6817V14.2707H70.4773V10.558ZM84.3828 6.23757V0.381215H85.554V14.2707H84.3828V7.25414H82.5375V13.5856H81.3772V0.712707H82.5375V6.23757H84.3828ZM76.5706 0.801105H77.7971V2.77901H80.659V3.78453H73.6866V2.77901H76.5706V0.801105ZM77.1783 4.72376C78.9242 4.72376 80.1728 6.02762 80.1839 7.87293C80.1728 9.74033 78.9242 11.0552 77.1783 11.0552C75.4435 11.0552 74.1839 9.74033 74.1949 7.87293C74.1839 6.02762 75.4435 4.72376 77.1783 4.72376ZM77.1783 9.98343C78.2833 9.99448 79.09 9.1105 79.09 7.87293C79.09 6.65746 78.2833 5.76243 77.1783 5.75138C76.0734 5.76243 75.2778 6.65746 75.2778 7.87293C75.2778 9.1105 76.0734 9.99448 77.1783 9.98343ZM94.4761 8.46961V11.4641H100.266V12.4917H87.5479V11.4641H93.2606V8.46961H89.0507V1.36464H90.2772V3.87293H97.4927V1.36464H98.7081V8.46961H94.4761ZM97.4927 4.87845H90.2772V7.46409H97.4927V4.87845ZM112.094 14.2707V0.381215H113.277V14.2707H112.094ZM106.89 5.36464H109.243V0.679558H110.404V13.5635H109.243V6.40331H106.89V5.36464ZM105.498 1.68508V4.32597C105.498 6.53591 106.58 8.92265 108.426 10.0166L107.719 10.9669C106.376 10.1602 105.42 8.67403 104.929 6.98895C104.42 8.82873 103.393 10.4199 102.006 11.2652L101.221 10.3481C103.144 9.22099 104.315 6.75691 104.315 4.38122V1.68508H105.498ZM124.762 7.67403V11.453H127.922V12.4807H115.204V11.453H118.353V7.70718C117.171 7.0884 116.453 6.0884 116.453 4.83425C116.453 2.67956 118.563 1.26519 121.525 1.25414C124.497 1.26519 126.607 2.67956 126.607 4.83425C126.607 6.07182 125.911 7.05525 124.762 7.67403ZM117.635 4.83425C117.635 6.38122 119.248 7.41989 121.525 7.41989C123.79 7.41989 125.425 6.38122 125.425 4.83425C125.425 3.27624 123.79 2.24862 121.525 2.25967C119.248 2.24862 117.635 3.27624 117.635 4.83425ZM119.558 8.16022V11.453H123.525V8.1547C122.922 8.30939 122.248 8.39226 121.525 8.39226C120.812 8.39226 120.154 8.30939 119.558 8.16022ZM130.502 13.0884C129.949 13.0884 129.485 12.6354 129.496 12.0829C129.485 11.5304 129.949 11.0773 130.502 11.0773C131.054 11.0773 131.507 11.5304 131.507 12.0829C131.507 12.6354 131.054 13.0884 130.502 13.0884Z"
          fill="#728094"
        />
      </svg>
    </div>
  );
}

export function HomeNavigation() {
  const { businessId } = useBusinessIdStore();
  const { user } = useCurrentUser();
  const { query } = useRouter();
  const LinkComponent = useLinkComponent();

  const handleClickSearchBar = () => {
    const searchText = query.searchText
      ? decodeURIComponent(query.searchText as string)
      : '';

    trackEvent('clicked searchBar in 마켓', {
      path: window.location.pathname,
      userId: user?.id,
      businessId,
      searchText,
    });

    openPageModal(SEARCH_ROUTER.searchText(searchText));
  };

  return (
    <TopNavigation
      className="z-20 !shadow-none [&>div]:bg-gray-000"
      left={
        <div className="flex items-center gap-8">
          <MarketTextSVG />
          <PlusMembershipTopBadgeContainer />
        </div>
      }
      right={
        <>
          <ShoppingCartLink />
          <LinkComponent href={MYPAGE_ROUTER.root} className="relative">
            <>
              <CouponTooltip />
              <TopNavigation.WrappedIcon key="user" icon="user" />
            </>
          </LinkComponent>
          <CashnyHeaderButton />
          <MenuButton />
        </>
      }
      bottom={
        <button
          type="button"
          onClick={handleClickSearchBar}
          className="block w-full"
        >
          <StaticSearchBar />
        </button>
      }
    />
  );
}

export function HomeNavigationSkeleton() {
  return (
    <TopNavigation
      left={
        <div className="flex items-center gap-8">
          <MarketTextSVG />
        </div>
      }
      bottom={<StaticSearchBar />}
    />
  );
}
